import { useQuery } from '@tanstack/react-query'
import { moduleDashboardQuery, type ModuleDashboard } from '../api/moduleDashboard'
import { moduleOf, shellQuery } from '../api/shell'
import { BoardTools } from './BoardTools'
import { BoardWindow } from './BoardWindow'
import { rangeOf, windowQuery, type BoardRange } from './windowRange'
import { DashboardCardSlot } from './DashboardCard'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * Tabloul de bord al unui MODUL - ce raspunde `Dashboard::module($modul)` in
 * portal, pentru Easeedesk, Inventoree si oricare altul.
 *
 * Aceleasi carduri, in aceeasi ordine, cu acelasi desen: HTML-ul unui card
 * vine gata facut din API (`CardMarkup`, adica exact `card.php`), la fel ca
 * pe tabloul central. Aceeasi foaie, `od_dashboard.css`.
 *
 * Bara de sus e cea a unui tablou de MODUL: cele doua perspective care au o
 * prima pagina scrisa (Tehnician, IT Manager), clasa de active si fereastra
 * de timp. Nu are banda de module, nici "New board"/"Arrange": prima e a
 * tabloului central, iar constructorul refuza oricum tablourile de modul -
 * butonul lui de acolo nu facea nimic.
 *
 * Fara carduri nu inseamna "gol": inseamna ori ca rolul n-are voie la
 * niciunul, ori ca n-a fost aleasa nicio firma. Se spune care din ele.
 *
 * DE CE E UNA SINGURA: pagina nu se schimba de la un modul la altul. Numai
 * capatul de API si adresa proprie difera, si alea intra ca parametri - vezi
 * `api/inventoreeBoard.ts`.
 *
 * `easeedesk/EaseedeskDashboardPage.tsx` are inca o copie a acestui ecran,
 * scrisa cu o zi inainte, pe care alt agent o avea deschisa cand s-a scris
 * asta. Cand se linisteste acolo, ea devine tot doua randuri, ca cea de la
 * Inventoree - nu se rescrie de sub cineva.
 */
export function ModuleBoardPage({
  query,
  basePath,
}: {
  /** Capatul modulului: `easeedeskDashboardQuery`, `inventoreeBoardQuery`, ... */
  query: (search: string) => ReturnType<typeof moduleDashboardQuery>
  /** Adresa paginii in /app, pe care se cladesc legaturile din bara. */
  basePath: string
}) {
  const search = typeof window === 'undefined' ? '' : window.location.search
  const page = useQuery(query(search))
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(['css/od_dashboard.css'], shell.data?.version ?? '')

  if (page.isPending || page.isError || !page.data) return <div className="odb-page" />

  return <ModuleBoardChrome page={page.data} basePath={basePath} />
}

/** `<basePath>?...` - adresa paginii, cu alegerile in ea. */
function href(basePath: string, view: string, days: number, klass: string, range: BoardRange | null): string {
  return `${basePath}?view=${view}&${windowQuery(days, klass, range)}`
}

function ModuleBoardChrome({ page, basePath }: { page: ModuleDashboard; basePath: string }) {
  const days = page.scope.days
  const klass = page.scope.class
  const view = page.board.view
  const range = rangeOf(page.scope)

  return (
    <div className="odb-page" data-based-on={view}>
      <div className="odb-bar-top">
        <nav className="odb-views">
          {Object.entries(page.views).map(([key, meta]) => (
            <a
              className={`odb-view${key === view ? ' is-on' : ''}`}
              title={meta.about}
              href={href(basePath, key, days, klass, range)}
              key={key}
            >
              <i className={`fas ${meta.icon}`} aria-hidden="true"></i> {meta.label}
            </a>
          ))}
        </nav>
        <nav className="odb-classes" aria-label="Asset class">
          {Object.entries(page.classes).map(([key, meta]) => (
            <a
              className={`odb-class${key === klass ? ' is-on' : ''}`}
              title={meta.about}
              href={href(basePath, view, days, key, range)}
              key={key}
            >
              <i className={`fas ${meta.icon}`} aria-hidden="true"></i> {meta.label}
            </a>
          ))}
        </nav>
        <BoardWindow
          windows={page.windows}
          days={days}
          range={range}
          said={page.scope.rangeSaid}
          maxDays={page.scope.maxDays}
          hrefFor={(n, chosen) => href(basePath, view, n, klass, chosen)}
        />
        {(page.noCompany || !page.history) && (
          <p className="odb-lead">
            {!page.history && (
              <span className="odb-lead-warn">
                The day-by-day lines under the numbers are not being recorded yet - the dashboard tables are missing. Apply
                the schema from Change Log; the first line appears the day after, when there are two days to draw.
              </span>
            )}
            {/* Fara randul "N of M cards could not be read - that is not the same as zero" (18 septembrie
                2026): omul il citea ca pe o eroare. Un card fara date isi spune singur, linistit, "No data
                yet" - vezi `CardMarkup.php`. `tally` ramane in raspuns, neschimbat. */}
            {page.noCompany && (
              <span className="odb-lead-warn">
                No company is selected, so the cards that count within one company have nothing to read. Pick one from the
                top bar.
              </span>
            )}
          </p>
        )}
      </div>
      {/* Panoul masinii si actiunile de pe card, cu aceeasi poarta ca a
          tabloului de modul (`Dashboard::boardRight`). */}
      <BoardTools days={days} klass={klass} />
      {page.cards.length === 0 ? (
        <div className="odb-empty">
          <i className="fas fa-lock" aria-hidden="true"></i> Nothing here is open to you. Ask an administrator for the
          rights, or pick another perspective above.
        </div>
      ) : (
        <div className="odb" id="odbGrid">
          {page.cards.map((card) => (
            <DashboardCardSlot card={card} edit={false} sizes={{}} key={card.id} />
          ))}
        </div>
      )}
    </div>
  )
}
